<script lang="ts">
  import { storyblokEditable } from '@storyblok/svelte';
  import type { Asset, TextMediaPageSectionBlock } from '@storyblok-types';
  import { PageSection } from '@athena-lib/Page';
  import { Column, Columns } from '@athena-lib/Columns';
  import clsx from 'clsx';
  import { Carousel, CarouselItem } from '@athena-lib/Carousel';
  import PageSectionHeading from './shared/PageSectionHeading.svelte';
  import { getFocalPointClasses } from './shared/image-utils';
  import ImageOrVideo from './ImageOrVideo.svelte';

  let { blok }: { blok: TextMediaPageSectionBlock } = $props();

  const imageClasses = {
    '1:1': 'aspect-square',
    '3:2': 'aspect-3/2',
    '2:3': 'aspect-2/3',
  };

  const imageWidthClasses = {
    large: 'w-full',
    medium: 'w-[75%]',
    small: 'w-[50%]',
  };
</script>

{#snippet media(asset: Asset)}
  <div class="flex justify-center">
    <div class={clsx(imageWidthClasses[blok.imageSize || 'large'])}>
      <ImageOrVideo
        autoplay={blok.autoplay}
        {asset}
        imageClass={clsx(blok.imageHeight)}
        innerImageClass={clsx(
          imageClasses[blok.imageHeight || '1:1'],
          getFocalPointClasses(asset),
          blok.mediaFit == 'cover' ? 'object-cover' : 'object-contain',
        )}
        videoClass={clsx(
          blok.mediaFit == 'cover' ? 'object-cover' : 'object-contain',
          'h-full w-full',
        )}
      />
    </div>
  </div>
{/snippet}

{#snippet text()}
  <div class="flex h-full items-center">
    <PageSectionHeading
      headingText={blok.headingText}
      headingTag={blok.headingTag}
      headingSize={blok.headingSize}
      description={blok.description}
      mobileAlignment={blok.mobileAlignment}
      desktopAlignment={blok.desktopAlignment}
      ctaButton={{
        text: blok.ctaButtonText,
        color: blok.ctaButtonColor,
        link: blok.ctaButtonLink,
        class: blok.ctaButtonExtraClasses,
      }}
    />
  </div>
{/snippet}

{#snippet images()}
  <div class="flex h-full items-center">
    {#if blok.images}
      {#if blok.images.length === 1}
        {@render media(blok.images[0])}
      {:else}
        <Carousel peekNext={true} itemCount={(blok.images ?? []).length}>
          {#each blok.images ?? [] as asset}
            <CarouselItem>
              {@render media(asset)}
            </CarouselItem>
          {/each}
        </Carousel>
      {/if}
    {/if}
  </div>
{/snippet}

<PageSection
  id={blok.elementId}
  action={storyblokEditable}
  actionArg={blok}
  ySpace={{ top: blok.topPadding ?? 'medium', bottom: blok.bottomPadding ?? 'medium' }}
  backgroundColor={blok.backgroundColor ?? 'light'}
>
  <Columns
    collapseBelow="md"
    reverseWhenCollapsed={blok.mobileImageFirst ?? false}
    space={{ sm: 'large', md: 'xxlarge' }}
    reverse={{
      sm: blok.mobileImageFirst ?? false,
      md: blok.desktopImageFirst ?? false,
      lg: blok.desktopImageFirst ?? false,
    }}
  >
    <Column width="1/2">
      {@render text()}
    </Column>
    <Column width="1/2">
      {@render images()}
    </Column>
  </Columns>
</PageSection>
